import React, { useState, useEffect } from 'react';
import { Table, Checkbox, Card, message, Input, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';

const CDifundirRomaneos = () => {
  const [mensajes, setMensajes] = useState([]);
  const [filteredMensajes, setFilteredMensajes] = useState([]);
  const [selectedNumeros, setSelectedNumeros] = useState([]);
  const [filteredSelectedNumeros, setFilteredSelectedNumeros] = useState([]);

  // Filtros separados para la tabla superior
  const [searchNumero, setSearchNumero] = useState('');
  const [searchCuenta, setSearchCuenta] = useState('');
  const [searchNombre, setSearchNombre] = useState('');

  // Filtros separados para la tabla inferior (seleccionados)
  const [selectedSearchNumero, setSelectedSearchNumero] = useState('');
  const [selectedSearchCuenta, setSelectedSearchCuenta] = useState('');
  const [selectedSearchNombre, setSelectedSearchNombre] = useState('');

  const user = JSON.parse(localStorage.getItem('user'));
  const empresaId = user?.user?.idCooperativa;

  useEffect(() => {
    const fetchMensajes = async () => {
      try {
        const mensajesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/mensajes/${empresaId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });

        const datosTransformados = mensajesResponse.data.flatMap(mensaje =>
          mensaje.datosCuenta.map(cuenta => ({
            key: `${mensaje.numero}-${cuenta.cuenta}`,
            numero: String(mensaje.numero || '').trim(),
            cuenta: String(cuenta.cuenta || '').trim(),
            nombre: cuenta.nombre || '',
          }))
        );

        setMensajes(datosTransformados);
        setFilteredMensajes(datosTransformados);

        const difundirResponse = await axios.get(`${process.env.REACT_APP_API_URL}/difundir/${empresaId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });

        if (difundirResponse.data?.romaneo?.telefonos) {
          const telefonos = difundirResponse.data.romaneo.telefonos.map(tel => ({
            key: `${tel.numero}-${tel.cuenta}`,
            numero: String(tel.numero || '').trim(),
            cuenta: String(tel.cuenta || '').trim(),
            nombre: tel.nombre || '',
          }));
          setSelectedNumeros(telefonos);
          setFilteredSelectedNumeros(telefonos);
        }
      } catch (error) {
        console.error('Error al cargar datos:', error);
        message.error('Error al cargar los datos.');
      }
    };

    fetchMensajes();
  }, [empresaId, user.token]);

  const isRecordSelected = (record) => {
    return selectedNumeros.some(
      (item) =>
        String(item.numero).trim() === String(record.numero).trim() &&
        String(item.cuenta).trim() === String(record.cuenta).trim()
    );
  };

  const handleCheckboxChange = (record) => {
    const isSelected = isRecordSelected(record);

    if (isSelected) {
      const newSelectedNumeros = selectedNumeros.filter(
        (item) => !(String(item.numero).trim() === String(record.numero).trim() && String(item.cuenta).trim() === String(record.cuenta).trim())
      );
      setSelectedNumeros(newSelectedNumeros);
      setFilteredSelectedNumeros(applySelectedFilter(newSelectedNumeros));
    } else {
      const newSelectedNumeros = [...selectedNumeros, { ...record }];
      setSelectedNumeros(newSelectedNumeros);
      setFilteredSelectedNumeros(applySelectedFilter(newSelectedNumeros));
    }
  };

  const handleGuardarLista = async () => {
    if (selectedNumeros.length === 0) {
      message.error('Debe seleccionar al menos un número.');
      return;
    }

    try {
      const telefonosData = selectedNumeros.map(({ numero, cuenta, nombre }) => ({
        numero,
        cuenta,
        nombre,
      }));

      await axios.post(
        `${process.env.REACT_APP_API_URL}/difundir`,
        { idCooperativa: empresaId, telefonos: telefonosData },
        { headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'application/json' } }
      );

      message.success('Lista de números guardada correctamente.');
    } catch (error) {
      console.error('Error al guardar la lista:', error);
      message.error('Error al guardar la lista de números.');
    }
  };

  const applyFilter = (data) => {
    return data.filter((item) => {
      const numero = String(item.numero || '').toLowerCase();
      const cuenta = String(item.cuenta || '').toLowerCase();
      const nombre = String(item.nombre || '').toLowerCase();

      return (
        (!searchNumero || numero.includes(searchNumero.toLowerCase())) &&
        (!searchCuenta || cuenta.includes(searchCuenta.toLowerCase())) &&
        (!searchNombre || nombre.includes(searchNombre.toLowerCase()))
      );
    });
  };

  const applySelectedFilter = (data) => {
    return data.filter((item) => {
      const numero = String(item.numero || '').toLowerCase();
      const cuenta = String(item.cuenta || '').toLowerCase();
      const nombre = String(item.nombre || '').toLowerCase();

      return (
        (!selectedSearchNumero || numero.includes(selectedSearchNumero.toLowerCase())) &&
        (!selectedSearchCuenta || cuenta.includes(selectedSearchCuenta.toLowerCase())) &&
        (!selectedSearchNombre || nombre.includes(selectedSearchNombre.toLowerCase()))
      );
    });
  };

  const handleSearchNumero = (e) => {
    setSearchNumero(e.target.value);
    setFilteredMensajes(applyFilter(mensajes));
  };

  const handleSearchCuenta = (e) => {
    setSearchCuenta(e.target.value);
    setFilteredMensajes(applyFilter(mensajes));
  };

  const handleSearchNombre = (e) => {
    setSearchNombre(e.target.value);
    setFilteredMensajes(applyFilter(mensajes));
  };

  const handleSelectedSearchNumero = (e) => {
    setSelectedSearchNumero(e.target.value);
    setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
  };

  const handleSelectedSearchCuenta = (e) => {
    setSelectedSearchCuenta(e.target.value);
    setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
  };

  const handleSelectedSearchNombre = (e) => {
    setSelectedSearchNombre(e.target.value);
    setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
  };

  const handleRemoveNumero = (numero, cuenta) => {
    const newSelectedNumeros = selectedNumeros.filter(
      (item) => !(String(item.numero).trim() === String(numero).trim() && String(item.cuenta).trim() === String(cuenta).trim())
    );
    setSelectedNumeros(newSelectedNumeros);
    setFilteredSelectedNumeros(applySelectedFilter(newSelectedNumeros));
  };

  const columns = [
    {
      title: '',
      dataIndex: 'checkbox',
      key: 'checkbox',
      width: 50,
      render: (_, record) => (
        <Checkbox checked={isRecordSelected(record)} onChange={() => handleCheckboxChange(record)} />
      ),
    },
    { title: 'Número de Teléfono', dataIndex: 'numero', key: 'numero' },
    { title: 'Número de Cuenta', dataIndex: 'cuenta', key: 'cuenta' },
    { title: 'Nombre de Cuenta', dataIndex: 'nombre', key: 'nombre' },
  ];

  const selectedColumns = [
    { title: 'Número de Teléfono', dataIndex: 'numero', key: 'numero' },
    { title: 'Número de Cuenta', dataIndex: 'cuenta', key: 'cuenta' },
    { title: 'Nombre de Cuenta', dataIndex: 'nombre', key: 'nombre' },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" danger onClick={() => handleRemoveNumero(record.numero, record.cuenta)}>
          Eliminar
        </Button>
      ),
    },
  ];

  const handleSelectAll = (selected) => {
    if (selected) {
      const newSelectedItems = [...selectedNumeros];
      filteredMensajes.forEach((record) => {
        if (!isRecordSelected(record)) {
          newSelectedItems.push(record);
        }
      });
      setSelectedNumeros(newSelectedItems);
      setFilteredSelectedNumeros(applySelectedFilter(newSelectedItems));
    } else {
      const newSelectedItems = selectedNumeros.filter(
        (item) => !filteredMensajes.some((record) => record.numero === item.numero && record.cuenta === item.cuenta)
      );
      setSelectedNumeros(newSelectedItems);
      setFilteredSelectedNumeros(applySelectedFilter(newSelectedItems));
    }
  };

  const areAllSelected =
    filteredMensajes.length > 0 && filteredMensajes.every((record) => isRecordSelected(record));

  return (
    <div className="p-4">
      <Card title="Difundir Romaneos" className="mb-4">
        <div style={{ display: 'flex', width: '100%', gap: '16px', marginTop: '16px', marginBottom: '16px', alignItems: 'center' }}>
          <Input
            value={searchNumero}
            onChange={handleSearchNumero}
            placeholder="Buscar por número de telefono"
            prefix={<SearchOutlined />}
            style={{ flex: '1 1 0', minWidth: '0' }}
          />
          <Input
            value={searchCuenta}
            onChange={handleSearchCuenta}
            placeholder="Buscar por cuenta"
            prefix={<SearchOutlined />}
            style={{ flex: '1 1 0', minWidth: '0' }}
          />
          <Input
            value={searchNombre}
            onChange={handleSearchNombre}
            placeholder="Buscar por nombre"
            prefix={<SearchOutlined />}
            style={{ flex: '1 1 0', minWidth: '0' }}
          />
          <Button
            onClick={() => {
              setSearchNumero('');
              setSearchCuenta('');
              setSearchNombre('');
              setFilteredMensajes(mensajes); // Restaurar la lista original
            }}
            style={{ marginLeft: 'auto' }} // Empuja el botón a la derecha
          >
            Limpiar filtros
          </Button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Checkbox checked={areAllSelected} onChange={(e) => handleSelectAll(e.target.checked)}>
            {areAllSelected ? 'Deseleccionar todos' : 'Seleccionar todos los filtrados'}
          </Checkbox>
        </div>

        <Table
          dataSource={filteredMensajes}
          columns={columns}
          rowKey={(record) => `${record.numero}-${record.cuenta}`}
          pagination={{ pageSize: 5 }}
        />

        <Card title="Números Seleccionados" style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', width: '100%', gap: '16px', marginTop: '16px', marginBottom: '16px', alignItems: 'center' }}>
            <Input
              value={selectedSearchNumero}
              onChange={handleSelectedSearchNumero}
              placeholder="Buscar por número de telefono"
              prefix={<SearchOutlined />}
              style={{ flex: '1 1 0', minWidth: 0 }}
            />
            <Input
              value={selectedSearchCuenta}
              onChange={handleSelectedSearchCuenta}
              placeholder="Buscar por cuenta"
              prefix={<SearchOutlined />}
              style={{ flex: '1 1 0', minWidth: 0 }}
            />
            <Input
              value={selectedSearchNombre}
              onChange={handleSelectedSearchNombre}
              placeholder="Buscar por nombre"
              prefix={<SearchOutlined />}
              style={{ flex: '1 1 0', minWidth: 0 }}
            />
            <Button
              onClick={() => {
                setSelectedSearchNumero('');
                setSelectedSearchCuenta('');
                setSelectedSearchNombre('');
                setFilteredSelectedNumeros(selectedNumeros); // Restaurar la lista original
              }}
              style={{ marginLeft: 'auto' }}
            >
              Limpiar filtros
            </Button>
          </div>
          <Table
            dataSource={filteredSelectedNumeros}
            columns={selectedColumns}
            rowKey={(record) => `${record.numero}-${record.cuenta}`}
            pagination={{ pageSize: 5 }}
          />
        </Card>

        <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
          <Button type="primary" onClick={handleGuardarLista}>
            Guardar Lista
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default CDifundirRomaneos;